import { useMemo, useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { Plus, Recycle } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid } from "@/components/shared/form";
import {
  MasterBanner,
  MasterFilterBar,
  MasterHeader,
  MasterPage,
  MasterSearchInput,
} from "@/components/shared/master-shell";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useInventoryBatches } from "@/features/inventory/api";
import { fmtQty } from "@/features/inventory/constants";
import { useMaterials } from "@/features/materials/api";
import { useStockLocations } from "@/features/locations/api";
import { useCreateScrapEntry, useScrapEntries } from "@/features/scrap/api";
import { emptyScrapForm, scrapFormSchema, type ScrapFormValues } from "@/features/scrap/schema";
import { isSupabaseConfigured } from "@/lib/supabase";

export function ScrapPage() {
  return (
    <ErrorBoundary title="Scrap crashed">
      <ScrapInner />
    </ErrorBoundary>
  );
}

function ScrapInner() {
  const { data = [], isLoading, error, refetch, isFetching } = useScrapEntries();
  const { data: materials = [] } = useMaterials();
  const { data: locations = [] } = useStockLocations();
  const { data: batches = [] } = useInventoryBatches({ batchStatus: "in_stock" });
  const create = useCreateScrapEntry();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const form = useForm<ScrapFormValues>({
    resolver: zodResolver(scrapFormSchema) as unknown as Resolver<ScrapFormValues>,
    defaultValues: emptyScrapForm,
  });

  const scrapYard = locations.find((l) => /scrap/i.test(`${l.code} ${l.name}`));

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return data;
    return data.filter((r) =>
      [r.reason, r.material_name, r.batch_no, r.location_code]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }, [data, q]);

  function openCreate() {
    form.reset({
      ...emptyScrapForm,
      location_id: scrapYard?.id ?? locations.find((l) => l.is_active)?.id ?? "",
    });
    setOpen(true);
  }

  async function onSubmit(values: ScrapFormValues) {
    await create.mutateAsync(values);
    setOpen(false);
  }

  if (!isSupabaseConfigured) {
    return (
      <EmptyState title="Supabase not configured" description="Configure Supabase for scrap." />
    );
  }

  return (
    <MasterPage>
      <MasterHeader
        title="Scrap"
        icon={<Recycle />}
        countLabel={`${filtered.length} entries`}
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        actions={
          <Button type="button" size="sm" onClick={openCreate}>
            <Plus />
            Record scrap
          </Button>
        }
      />

      <MasterBanner>
        Log wastage. Link a batch to deduct stock (issue movement); leave batch empty for log-only.
      </MasterBanner>

      <MasterFilterBar>
        <MasterSearchInput id="scrap-search" value={q} onChange={setQ} placeholder="Reason, material…" />
      </MasterFilterBar>

      {error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : isLoading ? (
        <p className="py-10 text-center text-xs text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No scrap recorded"
          description="Record scrap from coil edges, offcuts, or damaged stock."
          action={
            <Button type="button" size="sm" onClick={openCreate}>
              <Plus />
              Record scrap
            </Button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>When</TableHead>
                <TableHead>Material</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead className="text-right">Weight</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Location</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="tabular-nums text-xs text-muted-foreground">
                    {format(parseISO(r.created_at), "dd MMM yyyy HH:mm")}
                  </TableCell>
                  <TableCell className="text-xs font-medium">{r.material_name ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.batch_no ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums text-xs font-medium">
                    {fmtQty(r.weight)}
                  </TableCell>
                  <TableCell className="max-w-[12rem] truncate text-xs">{r.reason}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.location_code ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record scrap</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit(onSubmit)();
            }}
          >
            <FieldGrid>
              <Field label="Batch (optional)" span={12} htmlFor="sc-batch">
                <NativeSelect
                  id="sc-batch"
                  {...form.register("inventory_batch_id", {
                    onChange: (e) => {
                      const id = e.target.value;
                      const b = batches.find((x) => x.id === id);
                      if (b) {
                        form.setValue("material_id", b.material_id);
                        form.setValue("location_id", scrapYard?.id || b.location_id);
                      }
                    },
                  })}
                >
                  <option value="">— None (log only) —</option>
                  {batches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.batch_no} · {b.material_name} · {fmtQty(b.weight)} T
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field
                label="Material"
                required
                span={6}
                error={form.formState.errors.material_id?.message}
                htmlFor="sc-mat"
              >
                <NativeSelect id="sc-mat" {...form.register("material_id")}>
                  <option value="">Select…</option>
                  {materials
                    .filter((m) => m.is_active)
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                </NativeSelect>
              </Field>
              <Field
                label="Location"
                required
                span={6}
                error={form.formState.errors.location_id?.message}
                htmlFor="sc-loc"
              >
                <NativeSelect id="sc-loc" {...form.register("location_id")}>
                  <option value="">Select…</option>
                  {locations
                    .filter((l) => l.is_active)
                    .map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.code} · {l.name}
                      </option>
                    ))}
                </NativeSelect>
              </Field>
              <Field
                label="Weight (T)"
                required
                span={6}
                error={form.formState.errors.weight?.message}
                htmlFor="sc-wt"
              >
                <Input id="sc-wt" type="number" step="any" min={0} {...form.register("weight")} />
              </Field>
              <Field label="Qty (pcs)" span={6} htmlFor="sc-qty">
                <Input id="sc-qty" type="number" step={1} min={0} {...form.register("quantity")} />
              </Field>
              <Field
                label="Reason"
                required
                span={12}
                error={form.formState.errors.reason?.message}
                htmlFor="sc-reason"
              >
                <Input id="sc-reason" placeholder="Edge trim, open, damage…" {...form.register("reason")} />
              </Field>
              <Field label="Notes" span={12} htmlFor="sc-notes">
                <Input id="sc-notes" {...form.register("notes")} />
              </Field>
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={create.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </MasterPage>
  );
}
